import { ChangeDetectionStrategy, Component, EventEmitter, inject, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, map, takeUntil, tap } from 'rxjs';
import { SelectedVehiclesDictionary } from 'src/app/modules/core/models/custom-dictionary';
import { VehicleStore } from 'src/app/modules/core/store';
import { WidgetsService } from '@components/widgets/widgets.service';
import { CompactListColumn, CompactListItem } from 'src/app/modules/shared/models';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { ColumnFactory } from 'src/app/modules/shared/helpers/column-factory';
import { SpinnerComponent } from '../../../../modules/shared/components/spinner/spinner.component';
import { CompactListComponent } from '../../../../modules/shared/components/compact-list/compact-list.component';

import { TranslateModule } from '@ngx-translate/core';
import { FeedbackTextComponent } from '@modules/shared/components';

import _ from 'lodash';

@Component({
    selector: 'app-vehicle-selection-overview',
    templateUrl: './vehicle-selection-overview.component.html',
    styleUrls: ['./vehicle-selection-overview.component.scss'],
    imports: [CompactListComponent, SpinnerComponent, TranslateModule, FeedbackTextComponent],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class VehicleSelectionOverviewComponent implements OnInit, OnDestroy {
    @Output() nrItems = new EventEmitter<number>();

    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _router = inject(Router);
    private readonly _widgetsService = inject(WidgetsService);

    public items = signal<CompactListItem[] | undefined>(undefined);
    public columns: CompactListColumn[];

    private _selectedVehicles: SelectedVehiclesDictionary;

    private readonly _stateKey = 'vehicle-selection-overview-component-state';

    private readonly _ngUnsubscribe$ = new Subject<void>();

    ngOnInit() {
        this._initState();

        this._vehicleStore.getVehiclesState().pipe(
            map(vehicles => {
                if (!vehicles) {
                    // needed for the spinner to show instead of empty data-feedback
                    return [];
                } else {
                    return _.map(vehicles, vehicle => {
                        const item = new CompactListItem();
                        item.id = vehicle.inventoryNumber;
                        item.type = 'vehicle';
                        item.properties['inventoryNumber'] = vehicle.inventoryNumber;
                        item.properties['inventoryNumberWithoutPrefix'] = vehicle.inventoryNumberWithoutPrefix;
                        item.properties['garageCode'] = vehicle.garageCode;
                        item.properties['speed'] = vehicle.position?.speed;
                        item.properties['bearing'] = vehicle.position?.bearing;
                        item.properties['tripNumber'] = vehicle.tripNumber;
                        item.properties['blockNumber'] = vehicle.blockNumber;
                        item.properties['routeNumber'] = vehicle.routeNumber;
                        item.properties['warnings'] = vehicle.displayableWarnings?.length ?? 0;
                        item.properties['stateOfCharge'] = vehicle.stateOfCharge ? vehicle.stateOfCharge.sensorValue + '%' : '';
                        item.properties['onTrip'] = vehicle.onTrip;
                        item.properties['onVehicleTaskBlock'] = vehicle.onVehicleTaskBlock;
                        if (this._selectedVehicles) {
                            item.selected = _.includes(this._selectedVehicles.allValues, item.id);
                        }
                        return item;
                    });
                }
            }),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((items) => {
            this.items.set(items);
            this.nrItems.next(items?.length);
        });

        this._vehicleStore.getSelectedVehiclesState().pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((selectedVehicles: SelectedVehiclesDictionary) => {
            this._selectedVehicles = selectedVehicles;

            const currentItems = this.items();
            if (currentItems) {
                const updatedItems = _.map(currentItems, item => {
                    item.selected = _.includes(selectedVehicles?.allValues, item.id);
                    return item;
                });
                this.items.set(updatedItems);
            }
        });
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public itemSelected(item: any) {
        const encodedItemId = encodeURIComponent(item.id);
        const indexOfItemId = this._router.routerState.snapshot.url.indexOf(`outlet-right0:vehicle/${encodedItemId}`);
        if (indexOfItemId === -1) {
            this._widgetsService.openVehicle(String(item.id));
        }
    }

    public columnsUpdated(columns: CompactListColumn[]) {
        this._updateState();
    }

    private _initState() {
        this._initColumnDefaults();

        const stateData = localStorage.getItem(this._stateKey);
        if (stateData) {
            const state = Object.assign(new VehicleSelectionOverviewComponentState(), JSON.parse(stateData));
            this.columns = _.map(this.columns, (column) => {
                const storedColumn = _.find(state.columns, (c) => c.key === column.key);
                if (storedColumn) {
                    delete column.sort;
                    delete storedColumn.width;
                    delete storedColumn.sortNumeric;
                    delete storedColumn.icon;
                }
                return { ...column, ...storedColumn };
            });
        }
    }

    private _updateState() {
        let state = new VehicleSelectionOverviewComponentState();
        state = {
            ...state,
            columns: this.columns
        };
        localStorage.setItem(this._stateKey, JSON.stringify(state));
    }

    private _initColumnDefaults() {
        this.columns = [
            ColumnFactory.getInventoryColumn({ sort: 'asc' }),
            ColumnFactory.getRouteNumberColumn(),
            ColumnFactory.getTripNumberColumn(),
            ColumnFactory.getBlockNumberColumn(),
            ColumnFactory.getSpeedColumn(),
            ColumnFactory.getBearingColumn(),
            ColumnFactory.getWarningColumn({ visible: false }),
            ColumnFactory.getStateOfChargeColumm({ visible: false })
        ];
    }
}

export class VehicleSelectionOverviewComponentState {
    public columns: CompactListColumn[];
}
